<template>
  <v-navigation-drawer
    right
    app
    temporary
    :value="open"
    class="prvmd"
    width="80vw"
    @input="onDrawerInput"
  >
    <div
      ref="panel"
      class="prvmd__panel"
      role="dialog"
      aria-label="Pay range version"
      tabindex="-1"
      data-test="manage-drawer"
      @keydown.esc="emit('close')"
    >
      <PayRangeDrawerHeader
        :version-code="versionCode"
        :status="statusLabel"
        :state-badge="stateLabel"
        :show-badges="true"
        :show-state-badge="!isVoided"
        :show-chatter="chatterEnabled"
        :unread="chatterUnread"
        :corrected="Boolean(version?.correctedAt)"
        :corrected-at="version?.correctedAt || null"
        :corrected-by-name="versionCorrectedByName"
        @close="emit('close')"
        @chatter="emit('chatter', version)"
      />

      <div class="prvmd__body">
        <p v-if="rangeName" class="prvmd__identity" data-test="manage-identity">
          {{ rangeName }}
        </p>

        <p
          v-if="readOnlyRange"
          class="prvmd__note prvmd__note--warning"
          role="status"
          data-test="manage-range-note"
        >
          This pay range is {{ rangeStatusLabel }}. Versions are read-only.
          {{ rangeRestoreVerb }} the range from the page header.
        </p>
        <p
          v-if="isVoided"
          class="prvmd__note prvmd__note--neutral"
          role="status"
          data-test="manage-voided-note"
        >
          Voided versions are read-only.{{ chatterEnabled ? " Chatter stays available." : "" }}
        </p>
        <p
          v-if="isDraft"
          class="prvmd__note prvmd__note--neutral"
          role="status"
          data-test="manage-draft-note"
        >
          Draft · not published. No position uses it until you publish.
        </p>

        <section class="prvmd__card" aria-labelledby="prvmd-card-title">
          <header class="prvmd__card-header">
            <h3 id="prvmd-card-title" class="prvmd__card-title">Pay range version</h3>
            <span class="prvmd__card-version" data-test="manage-version-label">
              {{ shortVersionLabel }}
            </span>
          </header>

          <dl class="prvmd__facts">
            <div class="prvmd__fact">
              <dt class="prvmd__fact-label">Amounts</dt>
              <dd class="prvmd__fact-value" data-test="manage-amounts">{{ amountLabel }}</dd>
            </div>
            <div class="prvmd__fact">
              <dt class="prvmd__fact-label">Effective</dt>
              <dd class="prvmd__fact-value" data-test="manage-period">{{ periodLabel }}</dd>
            </div>
          </dl>

          <div v-if="!isDraft && !isVoided" class="prvmd__section" data-test="manage-usage">
            <h4 class="prvmd__section-title">Usage</h4>
            <p
              class="prvmd__section-text"
              :class="{ 'prvmd__section-text--info': usageCount > 0 }"
              :role="usageCount > 0 ? 'note' : null"
            >
              {{ usageLabel }}
            </p>
          </div>

          <div v-if="!isDraft && !isVoided" class="prvmd__section" data-test="manage-adjacent">
            <h4 class="prvmd__section-title">Adjacent versions (no gaps, no overlaps)</h4>
            <p class="prvmd__section-text">{{ previousLabel }}</p>
            <p class="prvmd__section-text">{{ nextLabel }}</p>
            <p v-if="impactLabel" class="prvmd__section-hint" data-test="manage-impact">
              {{ impactLabel }}
            </p>
          </div>
        </section>
      </div>

      <footer class="prvmd__footer" data-test="manage-footer">
        <p class="prvmd__footer-text">{{ footerText }}</p>

        <div v-if="actions.length" class="prvmd__actions">
          <button
            v-for="action in actions"
            :key="action.key"
            type="button"
            class="prvmd__btn"
            :class="`prvmd__btn--${action.variant}`"
            :disabled="saving"
            :data-test="`manage-action-${action.key}`"
            @click="emit(action.emit, version)"
          >
            {{ action.label }}
          </button>
        </div>
      </footer>
    </div>
  </v-navigation-drawer>
</template>

<script setup>
import { computed, nextTick, ref, watch } from "vue";
import PayRangeDrawerHeader from "@components/PayRangeDrawerHeader/PayRangeDrawerHeader.vue";
import { formatPayRangeAmount } from "@components/utils/formatPayRangeAmount";
import { versionStatusBadge } from "@components/FairPay/payRangeBadges";
import {
  localTodayIso,
  resolveVersionPeriodState,
} from "@components/PayRangeTimelineAndList/payRangeTimelineUtils";
import { findAdjacentVersions } from "@components/CorrectPayRangeVersionModal/correctPayRangeVersionUtils";
import { formatFairPayDate } from "@components/utils/fairPayFormat";

const props = defineProps({
  open: { type: Boolean, default: false },
  /**
   * History row of the version being managed. Shape:
   * { versionNumber, versionLabel, status: 'draft'|'voided'|<any other = In Force>,
   *   startDate, endDate, minAmount, maxAmount, usageCount }
   * State (Lapsed / Current / Future) is computed from startDate / endDate, never read from status.
   */
  version: { type: Object, default: null },
  /** All history rows of the range — used to find the adjacent versions. */
  versions: { type: Array, default: () => [] },
  /** Range lifecycle status: PUBLISHED | RETIRED | ARCHIVED. Retired/Archived is read-only. */
  rangeStatus: { type: String, default: "PUBLISHED" },
  rangeName: { type: String, default: "" },
  /** Range code, e.g. "P-1456" — combined into the version code "P-1456.V3". */
  code: { type: String, default: "" },
  unitSuffix: { type: String, default: "USD/year" },
  saving: { type: Boolean, default: false },
  chatterEnabled: { type: Boolean, default: false },
  chatterUnread: { type: Number, default: 0 },
  /** Display name of who corrected the version (Corrected badge tooltip). */
  correctedByName: { type: String, default: "" },
});

const emit = defineEmits([
  "close",
  "correct",
  "void",
  "new-version",
  "discard-draft",
  "edit-draft",
  "publish-draft",
  "chatter",
]);

const EN_DASH = "–";

const panel = ref(null);

const status = computed(() => props.version?.status || "");
const isDraft = computed(() => status.value === "draft");
const isVoided = computed(() => status.value === "voided");

const rangeStatusKey = computed(() => String(props.rangeStatus || "").toUpperCase());
const readOnlyRange = computed(() => ["RETIRED", "ARCHIVED"].includes(rangeStatusKey.value));
const rangeStatusLabel = computed(() =>
  rangeStatusKey.value === "ARCHIVED" ? "Archived" : "Retired"
);
const rangeRestoreVerb = computed(() =>
  rangeStatusKey.value === "ARCHIVED" ? "Unarchive" : "Reactivate"
);

const versionNumber = computed(() => props.version?.versionNumber ?? null);
const shortVersionLabel = computed(() =>
  isDraft.value || versionNumber.value == null ? "V?" : `V${versionNumber.value}`
);
const versionCode = computed(() => {
  const suffix = isDraft.value || versionNumber.value == null ? "?" : `V${versionNumber.value}`;
  return props.code ? `${props.code}.${suffix}` : suffix;
});

const statusLabel = computed(
  () => (versionStatusBadge(props.version) || versionStatusBadge("in-force")).label
);

/** The mapper resolves the name onto the history row; the prop is a fallback. */
const versionCorrectedByName = computed(
  () => props.correctedByName || props.version?.correctedByName || ""
);

function periodStateOf(version) {
  if (!version) return "";
  return resolveVersionPeriodState(
    {
      status: version.status,
      effectiveFrom: version.startDate,
      effectiveTo: version.endDate ?? null,
    },
    localTodayIso()
  );
}

/** State is computed, never stored: same helper as the timeline bar (versionToBarState). */
const stateKey = computed(() => periodStateOf(props.version) || "lapsed");

const stateLabel = computed(
  () => ({ lapsed: "Lapsed", future: "Future", current: "Current" })[stateKey.value]
);

const periodLabel = computed(() => {
  const start = formatFairPayDate(props.version?.startDate);
  const end = formatFairPayDate(props.version?.endDate);
  if (!start && !end) return "—";
  return `${start || "—"} ${EN_DASH} ${end || "open-ended"}`;
});

const currencyCode = computed(() => String(props.unitSuffix || "").split("/")[0] || "USD");

const amountLabel = computed(() => {
  const { minAmount, maxAmount } = props.version || {};
  if (minAmount == null && maxAmount == null) return "—";
  const format = (n) => (n == null ? "—" : formatPayRangeAmount(n));
  return `${currencyCode.value} ${format(minAmount)} ${EN_DASH} ${format(maxAmount)}`;
});

const usageCount = computed(() => Math.max(0, Number(props.version?.usageCount) || 0));

const usageLabel = computed(() => {
  if (usageCount.value === 0) return "Not used by any pay plan";
  const noun = usageCount.value === 1 ? "pay plan version" : "pay plan versions";
  return `Used by ${usageCount.value} committed ${noun}. Their snapshots are unchanged.`;
});

const adjacent = computed(() => findAdjacentVersions(props.versions, props.version));

function describeVersion(v) {
  const start = formatFairPayDate(v.startDate) || "—";
  const end = formatFairPayDate(v.endDate) || "present";
  return `Version ${v.versionNumber} (${start} ${EN_DASH} ${end})`;
}

const previousLabel = computed(() =>
  adjacent.value.previous
    ? `Previous · ${describeVersion(adjacent.value.previous)}`
    : "Previous · none, this is the earliest version"
);

const nextLabel = computed(() =>
  adjacent.value.next
    ? `Next · ${describeVersion(adjacent.value.next)}`
    : "Next · none, this is the latest version"
);

const impactLabel = computed(() => {
  const previous = adjacent.value.previous;
  if (!previous) return "";
  return `Voiding or correcting this version can move where Version ${previous.versionNumber} ends. Correct previews the impact before you confirm.`;
});

/** Rule 19: a new version needs a Current version and no Draft or Future version in the range. */
const canCreateVersion = computed(
  () =>
    stateKey.value === "current" &&
    !props.versions.some(
      (v) => v.status === "draft" || (v.status !== "voided" && periodStateOf(v) === "future")
    )
);

/** Rule 33 by State; a Retired/Archived range and Voided versions are read-only. */
const actions = computed(() => {
  if (!props.version || readOnlyRange.value || isVoided.value) return [];
  if (isDraft.value) {
    return [
      { key: "discard", label: "Discard", variant: "danger", emit: "discard-draft" },
      { key: "edit", label: "Edit Draft", variant: "secondary", emit: "edit-draft" },
      { key: "publish", label: "Publish Draft", variant: "primary", emit: "publish-draft" },
    ];
  }
  const list = [
    { key: "void", label: "Void", variant: "danger", emit: "void" },
    { key: "correct", label: "Correct", variant: "secondary", emit: "correct" },
  ];
  if (canCreateVersion.value) {
    list.push({ key: "new-version", label: "New version", variant: "primary", emit: "new-version" });
  }
  return list;
});

const footerText = computed(() => {
  if (!actions.value.length) {
    return `Read-only.${props.chatterEnabled ? " Chatter stays available." : ""}`;
  }
  if (isDraft.value) return "Publish makes this the next version of the range.";
  const correct =
    "Correct updates this version in place and marks it Corrected. Pay plans keep their snapshot.";
  return canCreateVersion.value
    ? `New version opens a proposal to change this range. ${correct}`
    : correct;
});

function onDrawerInput(value) {
  if (!value) emit("close");
}

watch(
  () => props.open,
  async (isOpen) => {
    if (!isOpen) return;
    await nextTick();
    panel.value?.focus?.();
  }
);
</script>

<style scoped>
.prvmd {
  height: 100vh;
  background-color: #ffffff;
}
.prvmd__panel {
  display: flex;
  flex-direction: column;
  height: 100%;
  gap: 24px;
  padding-top: 24px;
  background-color: #ffffff;
  border: 1px solid #eaecf0;
}
.prvmd__panel:focus {
  outline: none;
}
.prvmd__body {
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 16px;
  padding: 0 24px;
  overflow-y: auto;
}
.prvmd__identity {
  margin: 0;
  font-family: Inter, sans-serif;
  font-size: 14px;
  font-weight: 500;
  line-height: 20px;
  color: #475467;
}
.prvmd__note {
  margin: 0;
  padding: 10px 12px;
  border-radius: 8px;
  font-family: Inter, sans-serif;
  font-size: 14px;
  line-height: 20px;
}
.prvmd__note--warning {
  background-color: #fffcf5;
  border: 1px solid #fde3a8;
  color: #b54708;
}
.prvmd__note--neutral {
  background-color: #f9fafb;
  border: 1px solid #eaecf0;
  color: #475467;
}
.prvmd__card {
  display: flex;
  flex-direction: column;
  gap: 16px;
  padding: 16px;
  border: 1px solid #eaecf0;
  border-radius: 10px;
  background-color: #ffffff;
}
.prvmd__card-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
}
.prvmd__card-title {
  margin: 0;
  font-family: Inter, sans-serif;
  font-size: 14px;
  font-weight: 600;
  line-height: 20px;
  color: #101828;
}
.prvmd__card-version {
  font-family: Inter, sans-serif;
  font-size: 14px;
  font-weight: 600;
  line-height: 20px;
  color: #6941c6;
}
.prvmd__facts {
  display: flex;
  flex-wrap: wrap;
  gap: 24px;
  margin: 0;
}
.prvmd__fact {
  display: flex;
  flex-direction: column;
  gap: 2px;
}
.prvmd__fact-label {
  font-family: Inter, sans-serif;
  font-size: 12px;
  font-weight: 500;
  line-height: 16px;
  color: #667085;
}
.prvmd__fact-value {
  margin: 0;
  font-family: Inter, sans-serif;
  font-size: 14px;
  font-weight: 500;
  line-height: 20px;
  color: #101828;
}
.prvmd__section {
  display: flex;
  flex-direction: column;
  gap: 4px;
  padding-top: 12px;
  border-top: 1px solid #eaecf0;
}
.prvmd__section-title {
  margin: 0;
  font-family: Inter, sans-serif;
  font-size: 12px;
  font-weight: 600;
  line-height: 16px;
  color: #344054;
}
.prvmd__section-text,
.prvmd__section-hint {
  margin: 0;
  font-family: Inter, sans-serif;
  font-size: 14px;
  line-height: 20px;
  color: #475467;
}
.prvmd__section-text--info {
  color: #026aa2;
}
.prvmd__section-hint {
  font-size: 13px;
  color: #667085;
}
.prvmd__footer {
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 12px 24px;
  padding: 16px 24px;
  border-top: 1px solid #eaecf0;
}
.prvmd__footer-text {
  flex: 1 1 320px;
  margin: 0;
  font-family: Inter, sans-serif;
  font-size: 13px;
  line-height: 20px;
  color: #667085;
}
.prvmd__actions {
  display: flex;
  align-items: center;
  gap: 12px;
}
.prvmd__btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 10px 16px;
  border: 1px solid transparent;
  border-radius: 8px;
  font-family: Inter, sans-serif;
  font-size: 14px;
  font-weight: 600;
  line-height: 20px;
  white-space: nowrap;
  cursor: pointer;
  box-shadow: 0 1px 2px rgba(16, 24, 40, 0.05);
}
.prvmd__btn:focus-visible {
  outline: 2px solid #7f56d9;
  outline-offset: 2px;
}
.prvmd__btn:disabled {
  opacity: 0.45;
  cursor: not-allowed;
}
.prvmd__btn--primary {
  background: #7f56d9;
  border-color: #7f56d9;
  color: #ffffff;
}
.prvmd__btn--primary:hover:not(:disabled) {
  background: #6941c6;
  border-color: #6941c6;
}
.prvmd__btn--secondary {
  background: #ffffff;
  border-color: #d6bbfb;
  color: #6941c6;
}
.prvmd__btn--secondary:hover:not(:disabled) {
  background: #f9f5ff;
}
.prvmd__btn--danger {
  background: #ffffff;
  border-color: #fda29b;
  color: #b42318;
}
.prvmd__btn--danger:hover:not(:disabled) {
  background: #fef3f2;
}
</style>
